import { lazy, Suspense, useEffect, useState } from 'react';
import { Location, useLocation, useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { useIsMobileWidth } from '@/components/common/filter';
import { AiChatWindowTarget, aiChatWindowRequestState } from '@/state/aiChatWindowState';
import { isSamePlace, parseAiSheetPath, readAiBackground } from '../aiSheetRoute';

// 시트는 처음 열 때만 내려받는다 — AI 를 열지 않으면 받는 코드는 이 작은 부품뿐이다
const AiChatSheet = lazy(() => import('./AiChatSheet'));

/** 닫힐 때 내려가는 시간(AiChatSheet 의 AI_SHEET_EXIT_MS 와 같게 — 시트 파일을 미리 내려받지 않으려고 따로 둔다) */
const EXIT_MS: number = 240;

/**
 * 폰의 AI 시트를 띄우는 자리(App 에 하나) — 주소가 시트 칸(`/ai…` + `state.aiBackground`)이면 시트를 그린다.
 * 본문은 App 이 아래 깔린 화면(aiBackground)으로 그대로 그리므로 뒤 화면이 유지된다(aiSheetRoute.ts).
 *
 * - 시트 칸을 떠나 아래 화면으로 돌아오면(⌄·끌기·뒤로 가기) 닫히기 직전 주소로 잠깐 더 그려 내려가는 모습을 보여 준다.
 *   다른 화면으로 넘어가면(답변 아래 링크·글쓰기 등) 바로 내린다.
 * - PC 폭에서 시트 칸이 열려 있으면(폰에서 연 뒤 창을 넓힘) PC 방식대로 홈의 작은 창으로 옮긴다.
 */
const AiSheetHost = () => {
    const location: Location = useLocation();
    const navigate = useNavigate();
    const mobile: boolean = useIsMobileWidth();
    const setRequest = useSetRecoilState(aiChatWindowRequestState);
    const sheetTarget: AiChatWindowTarget | null = readAiBackground(location.state) ? parseAiSheetPath(location.pathname) : null;
    const showing: boolean = mobile && sheetTarget !== null;
    /** 닫히는 중인 시트의 주소 */
    const [leaving, setLeaving] = useState<Location | null>(null);
    /** 마지막으로 그린 시트 주소 */
    const [lastShown, setLastShown] = useState<Location | null>(null);

    // 닫히는 상태는 그리는 중에 바로 정한다 — 효과(effect)에서 정하면 주소가 바뀐 첫 그림에서 시트가 한 번 빠졌다가 다시 붙어,
    // 끌던 위치가 사라지고 열기 애니메이션이 다시 돌아 위로 튀었다가 사라졌다(2026-10-07 시뮬레이터 녹화)
    if (showing) {
        if (lastShown !== location) setLastShown(location);
        if (leaving) setLeaving(null);
    } else if (lastShown) {
        const background = readAiBackground(lastShown.state);
        setLeaving(mobile && background && isSamePlace(background, location) ? lastShown : null);
        setLastShown(null);
    }

    useEffect(() => {
        if (!leaving) return undefined;
        const timer: number = window.setTimeout(() => setLeaving(null), EXIT_MS);
        return () => window.clearTimeout(timer);
    }, [leaving]);

    // PC 폭 — 홈의 작은 창으로 옮긴다(AiWindowRedirect 와 같은 길)
    useEffect(() => {
        if (mobile || !sheetTarget) return;
        setRequest(sheetTarget);
        navigate('/', { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mobile, sheetTarget?.view, sheetTarget?.chatId, sheetTarget?.shareToken]);

    const sheetLocation: Location | null = showing ? location : leaving;
    if (!sheetLocation) return null;
    return (
        <Suspense fallback={null}>
            <AiChatSheet location={sheetLocation} leaving={!showing} />
        </Suspense>
    );
};

export default AiSheetHost;
